import React, { useState, useCallback, useEffect } from 'react';
import { getTaskPendingCount, getIntakeActiveCount, getConversations, getDashboard } from './utils/api';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { MobileProvider, useMobile } from './context/MobileContext';
import Sidebar from './components/layout/Sidebar';

// Pages
import LoginPage from './pages/LoginPage';
import InvitePage from './pages/InvitePage';
import Dashboard from './pages/Dashboard';
import CasesPage from './pages/CasesPage';
import CaseDetail from './pages/CaseDetail';
import TasksPage from './pages/TasksPage';
import BillingPage from './pages/BillingPage';
import CalendarPage from './pages/CalendarPage';
import CommunicationsPage from './pages/CommunicationsPage';
import AuditLogPage from './pages/AuditLogPage';
import UpgradePage  from './pages/UpgradePage';
import NetworkPage from './pages/NetworkPage';
import SettingsPage from './pages/SettingsPage';
import DocumentTemplatesPage from './pages/DocumentTemplatesPage';
import LettersFormsPage from './pages/LettersFormsPage';
import ProfilePage from './pages/ProfilePage';
import ReportsPage from './pages/ReportsPage';
import IntakePage from './pages/IntakePage';
import FormBuilderPage from './pages/FormBuilderPage';
import PublicFormPage from './pages/PublicFormPage';
import ImpersonatePage from './pages/ImpersonatePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SignPage from './pages/SignPage';
import SupportPage from './pages/SupportPage';
import MessengerPage from './pages/MessengerPage';
import SubscriptionBanner from './components/SubscriptionBanner';

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function AppLayout({ children }) {
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [taskCount,     setTaskCount]     = useState(0);
  const [intakeCount,   setIntakeCount]   = useState(0);
  const [alertCount,    setAlertCount]    = useState(0);
  const { sidebarOpen, closeSidebar, isMobile } = useMobile();

  // Poll pending task count, active intake count, and unread messages
  useEffect(() => {
    const fetchCounts = () => {
      getTaskPendingCount().then(r => setTaskCount(r?.count || 0)).catch(() => {});
      getIntakeActiveCount().then(r => setIntakeCount(r?.count || 0)).catch(() => {});
      getDashboard().then(r => setAlertCount(r?.alerts_count || 0)).catch(() => {});
    };
    const fetchMessages = () => {
      getConversations().then(data => {
        const list = Array.isArray(data) ? data : [];
        const count = list.filter(c => c.has_unread).length;
        setUnreadCount(count);
        const base = 'ZeusCMS';
        document.title = count > 0 ? `(${count}) ${base}` : base;
      }).catch(() => {});
    };
    fetchCounts();
    fetchMessages();
    const countIv = setInterval(fetchCounts, 60000);    // tasks + intakes + alerts every 60s
    const msgIv   = setInterval(fetchMessages, 15000);  // messages every 15s
    return () => { clearInterval(countIv); clearInterval(msgIv); };
  }, []);

  const handleMessagesClick = useCallback(() => {
    // Open messenger in its own browser window
    const w = 820;
    const h = 700;
    const left = window.screenX + window.outerWidth - w - 40;
    const top = window.screenY + 60;
    const features = `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`;
    window.open('/messenger', 'zeuscms-messenger', features);
  }, []);

  return (
    <div className="app-layout">
      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-backdrop" onClick={closeSidebar} />
      )}

      <Sidebar
        onMessagesClick={handleMessagesClick}
        unreadCount={unreadCount}
        taskCount={taskCount}
        intakeCount={intakeCount}
        alertCount={alertCount}
      />
      <main className="app-main">
        <SubscriptionBanner />
        {children}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <MobileProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/impersonate"    element={<ImpersonatePage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/forms/submit/:token" element={<PublicFormPage />} />
        <Route path="/sign/:token" element={<SignPage />} />

        {/* Messenger — standalone window (no sidebar/header, handles own auth) */}
        <Route path="/messenger" element={<MessengerPage />} />

        {/* Protected routes */}
        <Route path="/" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
        <Route path="/cases" element={<ProtectedRoute><AppLayout><CasesPage /></AppLayout></ProtectedRoute>} />
        <Route path="/cases/:id" element={<ProtectedRoute><AppLayout><CaseDetail /></AppLayout></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><AppLayout><TasksPage /></AppLayout></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><AppLayout><BillingPage /></AppLayout></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><AppLayout><CalendarPage /></AppLayout></ProtectedRoute>} />
        <Route path="/communications" element={<ProtectedRoute><AppLayout><CommunicationsPage /></AppLayout></ProtectedRoute>} />
        <Route path="/network" element={<ProtectedRoute><AppLayout><NetworkPage /></AppLayout></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><AppLayout><ReportsPage /></AppLayout></ProtectedRoute>} />
        <Route path="/letters" element={<ProtectedRoute><AppLayout><LettersFormsPage /></AppLayout></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><AppLayout><DocumentTemplatesPage /></AppLayout></ProtectedRoute>} />
        <Route path="/form-builder/new" element={<ProtectedRoute><AppLayout><FormBuilderPage /></AppLayout></ProtectedRoute>} />
        <Route path="/form-builder/:id" element={<ProtectedRoute><AppLayout><FormBuilderPage /></AppLayout></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
        <Route path="/upgrade" element={<ProtectedRoute><AppLayout><UpgradePage /></AppLayout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
        <Route path="/intake" element={<ProtectedRoute><AppLayout><IntakePage /></AppLayout></ProtectedRoute>} />
        <Route path="/audit-log" element={<ProtectedRoute><AppLayout><AuditLogPage /></AppLayout></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><AppLayout><SupportPage /></AppLayout></ProtectedRoute>} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </MobileProvider>
  );
}
